import React, { ReactNode } from 'react';
import {
  BoxTypes,
  CouponTypeValue,
  NSB_SKYPOD_MONTHLY_AND_ONEOFF_FEE_ON_US,
  productSkuIds,
  productStoreApi,
  ServiceStatus,
  T_Product,
  useboxesStore,
  useCartContainer,
  useAnalytics,
  KonakartService,
  useHandleRemove,
  useOrderStore,
  useCouponStore,
  useProductInCart,
  PACKAGE_TYPE,
} from '@sky-tv-group/shared';
import { Coupon, PromoCardFragment } from '@sky-tv-group/graphql';
import MoreChoicesForYou, { MoreChoicesForYouProduct } from './MoreChoicesForYou';
import { CarouselPlaceholder } from './Placeholder';
import { EligibilityType, REACT_APP_ACCOUNT_URL } from '../../config';
interface MoreChoicesForYouProps {
  myAccountV2SWR: any;
  kkService: KonakartService;
  journeyType?: string;
  kkProducts?: T_Product[];
  bannerContent?: ReactNode;
}

export const MoreChoicesForYouContainer = ({
  myAccountV2SWR,
  kkService,
  journeyType = 'MyAccount',
  kkProducts,
  bannerContent,
}: MoreChoicesForYouProps) => {

  const { coupons } = useCouponStore();
  const handleRemove = useHandleRemove(kkService);
  const { toggleProduct, getProductTypeByCoupon } = useCartContainer(
    kkService,
    CouponTypeValue.Upgrade
  );
  const { order } = useOrderStore();
  const { checkoutStarted } = useAnalytics();
  let myAccount_BaseUrl = REACT_APP_ACCOUNT_URL;

  const userInfo = myAccountV2SWR.useUserInformationGQL();
  const choices = myAccountV2SWR.useGetMoreChoicesForYou();

  let allpromos: PromoCardFragment[] = choices?.data?.promoCards ?? [];
  // filter packages by "Package" types, also if they have
  // an existing promotion or if subscribed
  let packages = (choices?.data?.productsCards ?? [])
    .filter((pkg: any) => pkg.type === 'Package')
    .filter((pkg: any) => !allpromos.some(promo => promo.sku?.split(',').some(sku => sku.trim() === pkg.sku)))
    .filter(
      (pkg: any) =>
        !userInfo?.data?.billing?.products.some(
          (product: any) => product.serviceStatus === ServiceStatus.Active && product.sku === pkg.sku
        )
    );

  const boxes = useboxesStore(s => s.boxes);
  // offer banner should always be visible until customer reaches the max limit
  const isBoxReachesMaxLimit =
    (boxes?.filter(b => b?.boxType === BoxTypes.NEW || b.boxType === BoxTypes.UPGRADE)?.length ?? 0) >= 5;
  // filter promo cards if they are already in store or product(s) is subscribed
  let activeBillingCoupons = userInfo?.data?.billing?.campaigns
    ?.filter((c: { active: boolean }) => c.active === true)
    .map((c: { promotionCode: any }) => c.promotionCode);

  let promos = allpromos
    .filter(promo => promo?.eligibleFor !== EligibilityType.ContractDiscount && promo?.eligibleFor !== EligibilityType.SKY_REWARDS)
    .filter(promo => promo?.eligibilityIncludeServiceCode != '00000')
    .filter(
      promo => (promo?.coupon?.couponCode === NSB_SKYPOD_MONTHLY_AND_ONEOFF_FEE_ON_US ? !isBoxReachesMaxLimit : true) //!coupons.some(c => c?.couponCode === promo?.coupon?.couponCode)
    )
    .filter(
      promo =>
        //Need to check whether existing NSB has One off fee before skiping
        promo?.eligibilityIncludeServiceCode === productSkuIds.arrowBox.primary ||
        promo?.eligibilityIncludeServiceCode === productSkuIds.arrowBoxBlack.primary ||
        userInfo?.data?.billing?.products.filter(
          (product: any) =>
            product.serviceStatus === ServiceStatus.Active &&
            promo?.eligibilityIncludeServiceCode?.split(',')?.find((s: string) => s === product.sku)
        ).length !== promo?.eligibilityIncludeServiceCode?.split(',').length
    );

  // Apply Recontracting Filter
  let promos_Recontract = allpromos
    .filter(promo => promo?.eligibleFor === EligibilityType.ContractDiscount)
    .filter(
      promo =>
        !promo?.eligibilityIncludeCampaign
          ?.split(',')
          .every((campaign: string) => activeBillingCoupons?.includes(campaign))
    );
  promos = [...promos, ...promos_Recontract];

  /* Filter TV promos for BB only customers. */
  const broadbandOnlyAccount = userInfo?.data?.billing?.broadbandOnlyAccount;
  if (broadbandOnlyAccount) {
    promos = promos.filter(promo => getProductTypeByCoupon(promo.coupon!) === 'BROADBAND');
  }
  const { starterInCart } = useProductInCart();
  const coupon = coupons[0];
  const handleProductCardCTA = async (inCart: boolean, product?: T_Product) => {
    if (product) {
      if (journeyType == "MyAccount"){
        if (inCart) {
          await handleRemove(product);
        } else {
          await toggleProduct(product, true);
          order && checkoutStarted(order, false, coupon, '', false, starterInCart ? PACKAGE_TYPE.DTH : PACKAGE_TYPE.BROADBAND);
        }
      } else{
          window.location.href = `${myAccount_BaseUrl}mp/checkout?skus=` + product?.sku;
      }
    }
  };

  const handlePromoCardCTA = async (type: string, coupon: Coupon | null, promoCard: any) => {
    switch (type) {
      case 'Box':
        // history.push(`/mp/wizard/sky-box`);
        window.location.href = myAccount_BaseUrl + 'mp/wizard/sky-box';
        break;
      case 'Data':
        // history.push(`/mp/wizard/broadband`);
        window.location.href = myAccount_BaseUrl + 'mp/wizard/broadband';
        break;
      case 'Product':
        if (coupon) {
          const params = new URLSearchParams({
            includeSkus: promoCard?.eligibilityIncludeServiceCode || '',
            excludeSkus: promoCard?.eligibilityExcludeServiceCode || '',
            includeCoupon: promoCard?.eligibilityIncludeCampaign || '',
            excludeCoupon: promoCard?.eligibilityExcludeCampaign || '',
          });

          const checkoutUrl = `${myAccount_BaseUrl}mp/checkout?isEEOffer=true&${params.toString()}`;
          window.location.href = checkoutUrl;
        }
        break;
      default:
        break;
    }
  };

  const available: MoreChoicesForYouProduct[] = packages.reduce((prev: MoreChoicesForYouProduct[], curr: any) => {
    const kkProductData  = journeyType !== "MyAccount" ? kkProducts : productStoreApi.getState().products ?? [];
    const kkProduct = kkProductData?.find((p: T_Product) => p.sku === curr.sku);
    if (!userInfo?.data.billing.broadbandOnlyAccount && kkProduct) {
      prev.push({
        product: curr,
        kkProduct: kkProduct,
      });
    }
    return prev;
  }, []);

  if (userInfo?.error || choices?.error) {
    return <CarouselPlaceholder />;
  }

  if (!choices || choices?.loading) {
    return <CarouselPlaceholder errored={false} />;
  }


  if (available?.length > 0 || promos?.length > 0) {
    return (
      <MoreChoicesForYou
        choices={available}
        kkService={kkService}
        // @ts-ignore
        promos={promos}
        handleProductCardCTA={handleProductCardCTA}
        journeyType={journeyType}
        handlePromoCardCTA={handlePromoCardCTA}
      />
    );
  } else if (journeyType !== 'MyAccount' && bannerContent) {
    return (<div>{bannerContent}</div>);
  }
  return <span />;
};
